import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getTranslations } from '../utils/translations';
import './Destinations.css';

const destinationData = [
  {
    key: 'vienna',
    name: "Vienna, Austria",
    img: "https://images.unsplash.com/photo-1516550893923-42d28e5677af?auto=format&fit=crop&w=600&q=80",
    basePrice: "€799",
    offers: [
      { typeKey: 'withDriver', price: "€1,199", icon: "🚗" },
      { typeKey: 'carRental', price: "€999", icon: "🚙" },
      { typeKey: 'touristGuide', price: "€1,099", icon: "👨‍🏫" }
    ]
  },
  {
    key: 'krakow',
    name: "Krakow, Poland",
    img: "/images/krakow.jpg",
    basePrice: "€549",
    offers: [
      { typeKey: 'withDriver', price: "€849", icon: "🚗" },
      { typeKey: 'carRental', price: "€699", icon: "🚙" },
      { typeKey: 'touristGuide', price: "€799", icon: "👨‍🏫" }
    ]
  },
  {
    key: 'warsaw',
    name: "Warsaw, Poland",
    img: "/images/warsaw.jpg",
    basePrice: "€599",
    offers: [
      { typeKey: 'withDriver', price: "€899", icon: "🚗" },
      { typeKey: 'carRental', price: "€749", icon: "🚙" },
      { typeKey: 'touristGuide', price: "€849", icon: "👨‍🏫" }
    ]
  },
  {
    key: 'budapest',
    name: "Budapest, Hungary",
    img: "/images/budapest.jpg",
    basePrice: "€649",
    offers: [
      { typeKey: 'withDriver', price: "€949", icon: "🚗" },
      { typeKey: 'carRental', price: "€799", icon: "🚙" },
      { typeKey: 'touristGuide', price: "€899", icon: "👨‍🏫" }
    ]
  },
  {
    key: 'hallstatt',
    name: "Hallstatt, Austria",
    img: "/images/hallstatt.jpg",
    basePrice: "€899",
    offers: [
      { typeKey: 'withDriver', price: "€1,299", icon: "🚗" },
      { typeKey: 'carRental', price: "€1,099", icon: "🚙" },
      { typeKey: 'touristGuide', price: "€1,199", icon: "👨‍🏫" }
    ]
  },
  {
    key: 'paris',
    name: "Paris, France",
    img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=600&q=80",
    basePrice: "€899",
    offers: [
      { typeKey: 'withDriver', price: "€1,399", icon: "🚗" },
      { typeKey: 'carRental', price: "€1,199", icon: "🚙" },
      { typeKey: 'touristGuide', price: "€1,299", icon: "👨‍🏫" }
    ]
  },
  {
    key: 'rome',
    name: "Rome, Italy",
    img: "https://images.unsplash.com/photo-1515542622106-78bda8ba0e5b?auto=format&fit=crop&w=600&q=80",
    basePrice: "€749",
    offers: [
      { typeKey: 'withDriver', price: "€1,199", icon: "🚗" },
      { typeKey: 'carRental', price: "€999", icon: "🚙" },
      { typeKey: 'touristGuide', price: "€1,099", icon: "👨‍🏫" }
    ]
  },
  {
    key: 'zakopane',
    name: "Zakopane, Poland",
    img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=600&q=80",
    basePrice: "€699",
    offers: [
      { typeKey: 'withDriver', price: "€1,099", icon: "🚗" },
      { typeKey: 'carRental', price: "€899", icon: "🚙" },
      { typeKey: 'touristGuide', price: "€999", icon: "👨‍🏫" }
    ]
  },
  {
    key: 'dubai',
    name: "Dubai, UAE",
    img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=600&q=80",
    basePrice: "€1,199",
    offers: [
      { typeKey: 'withDriver', price: "€1,699", icon: "🚗" },
      { typeKey: 'carRental', price: "€1,399", icon: "🚙" },
      { typeKey: 'touristGuide', price: "€1,499", icon: "👨‍🏫" }
    ]
  }
];

export default function Destinations() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = getTranslations(language);
  const trackRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const destinations = destinationData.map(dest => ({
    ...dest,
    desc: t.destinationsData[dest.key],
    offers: dest.offers.map(offer => ({
      ...offer,
      type: t.destinations[offer.typeKey]
    }))
  }));

  const updateScrollState = () => {
    const track = trackRef.current;
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    setCanScrollPrev(track.scrollLeft > 4);
    setCanScrollNext(track.scrollLeft < maxScroll - 4);
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    updateScrollState();
    track.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      track.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [destinations.length]);

  const getScrollAmount = () => {
    const track = trackRef.current;
    if (!track) return 0;
    const card = track.querySelector('.destination-card');
    if (!card) return track.clientWidth * 0.85;
    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return card.getBoundingClientRect().width + gap;
  };

  const scrollByCard = (direction) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * getScrollAmount(), behavior: 'smooth' });
  };

  return (
    <section id="destinations" className="destinations">
      <div className="destinations-container">
        <h2 className="destinations-title">{t.destinations.title}</h2>
        <p style={{textAlign: 'center', color: '#666', marginBottom: '2rem', fontSize: '1.1rem'}}>{t.destinations.subtitle}</p>

        <div className="destinations-carousel">
          <button
            type="button"
            className="destinations-arrow destinations-arrow-prev"
            onClick={() => scrollByCard(-1)}
            disabled={!canScrollPrev}
            aria-label="Previous destinations"
          >
            ‹
          </button>

          <div
            className="destinations-track"
            ref={trackRef}
            tabIndex={0}
            role="region"
            aria-label={t.destinations.title}
          >
            {destinations.map((dest, index) => (
              <div key={dest.name} className="destination-card" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="destination-image">
                  <img src={dest.img} alt={dest.name} />
                </div>
                <div className="destination-content">
                  <h3>{dest.name}</h3>
                  <p>{dest.desc}</p>
                  
                  <div style={{marginTop: '1.5rem'}}>
                    <button 
                      className="destination-button destination-button-explore"
                      onClick={() => navigate(`/blog/${dest.key}`)}
                    >
                      Explore
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="destinations-arrow destinations-arrow-next"
            onClick={() => scrollByCard(1)}
            disabled={!canScrollNext}
            aria-label="Next destinations"
          >
            ›
          </button>
        </div>
      </div>
    </section>
  );
}
